<script>
import BtnComoponent from './Btn.vue'
import DateFieldsComponent from './DateFields.vue'
import DropdownComponent from './Dropdown.vue'
import GuestFieldsComponent from './GuestFields.vue'
import IconComponent from './Icon.vue'
import RatePlanToggleComponent from './RatePlanToggle.vue'
import { marked } from 'marked'
import store from '../store.js'
import { breakpointMap } from '../breakpoint'
import { get as getAvailability } from '../services/availability'
import { getDiffDays, getDateString, normalizeDate } from '../date'

let uuid = 0

export default {
    // state
    data () {
        const id = uuid++
        const submitBtnId = `vca_vue_prop_page_search_submit_btn_` + id
        return {
            action: null,
            loading: null,
            stayLengthMax: 28,
            stayLengthMaxErrorTimeDuration: 1500,
            stayLengthMaxErrorTimeout: null,
            showStayLengthMaxErrorMsg: null,
            resultsHTML: null,
            resultsError: null,
            store,
            submitBtnId,
            // availability
            availability: null,
            hasAvailabilityData: null,
            isGettingAvailability: null,
            // date
            arrival: null,
            departure: null,
            startDate: null,
            endDate: null,
            // property
            propertyRentalSourceId: null,
            // room types
            roomType: null,
            roomTypeOptions: null,
            // search params
            checkInDate: null,
            checkOutDate: null,
            numberOfGuests: null,
            numberOfChildren: null,
            childAges: [],
            // user
            userId: null,
            isPointsMember: null,
            loggedIn: null,
            points: null,
            // window
            breakpointMap,
            // lifecycle
            isMounted: false,
        }
    },
    props: {
        property: String,
        search: String,
        user: String,
        resultsErrorMarkdown: String,
    },
    computed: {
        arrivalDate () {
            return this.arrival && new Date(this.arrival)
        },
        departureDate () {
            return this.departure && new Date(this.departure)
        },
        datefields () {
            return [
                {
                    display: this.checkInDate,
                    event: 'update-arrival',
                    key: 'arrival',
                    label: 'Arriving',
                    value: this.arrival,
                },
                {
                    display: this.checkOutDate,
                    event: 'update-departure',
                    key: 'departure',
                    label: 'Departing',
                    value: this.departure,
                },
            ]
        },
        guests () {
            return {
                adults: this.numberOfGuests,
                children: this.childAges,
            }
        },
        ratePlan () {
            if (this.hasAvailabilityData) {
                return this.points ? 'points' : 'usd'
            }
            return null
        },
        roomTypeDropdown () {
            const isFullWidth = store.breakpoint.includes('s')
            return {
                from: 'right',
                isFullWidth,
                label: 'Room Type',
                labelIsHidden: isFullWidth,
                options: this.roomTypeOptions,
                value: this.roomType,
            }
        },
        hasRatePlanToggle () {
            return this.loggedIn && this.isPointsMember
        },
        stayLength () {
            return this.getStayLength(this.arrivalDate, this.departureDate)
        },
        resultsErrorMarkup () {
            return this.resultsErrorMarkdown ? marked.parse(this.resultsErrorMarkdown) : null
        },
    },
    methods: {
        getStayLength (arrivalDate, departureDate) {
            if (arrivalDate && departureDate) {
                return getDiffDays([ departureDate, arrivalDate ])
            } else {
                return 0
            }
        },
        setRoomType (roomType) {
            this.roomType = roomType
            this.updateAvailability()
        },
        submit (onPageLoad = false) {
            if (!this.$el || this.stayLength < 1 || this.stayLength > this.stayLengthMax) return

            this.resultsHTML = null
            this.resultsError = false

            this.loading = true

            const url = new URL(window.location.origin)
            url.pathname = this.$el.querySelector('form').getAttribute('action')
            url.searchParams.append('checkInDate', this.checkInDate)
            url.searchParams.append('checkOutDate', this.checkOutDate)
            url.searchParams.append('numberOfGuests', this.numberOfGuests)
            url.searchParams.append('numberOfChildren', this.childAges.length)
            this.childAges.forEach(e => url.searchParams.append('childAges[]', e))
            url.searchParams.append('points', this.points)

            if (!onPageLoad) {
                window?.history?.pushState(null, null, url.search)
            }

            fetch(url)
                .then(response => {
                    if (!response.ok) {
                        this.loading = false;
                        this.resultsError = true;
                        return null;
                    }
                    return response.text();
                })
                .then(html => {
                    if (html !== null) {
                        this.loading = false;
                        this.resultsHTML = html;
                    }
                })
                .catch(() => {
                    this.loading = false;
                    this.resultsError = true;
                })
        },
        updateAvailability () {
            if (!this.hasAvailabilityData) return
            this.isGettingAvailability = true
            const params = {
                propertyRentalSourceId: this.propertyRentalSourceId,
                numberOfBedrooms: this.roomType,
                startDate: getDateString(this.startDate),
                endDate: getDateString(this.endDate),
            }
            if (typeof this.userId === 'number') {
                params.userId = this.userId
            }
            getAvailability({ params })
                .then(r => r.json())
                .then(json => {
                    this.availability = json.results
                    this.isGettingAvailability = false
                })
        },
        updateArrival ($event) {
            const date = $event?.detail.date
            if (date) {
                if (date >= this.departureDate || this.getStayLength(date, this.departureDate) > this.stayLengthMax) {
                    this.departure = null
                }
                this.arrival = date.toISOString().slice(0,10)
            } else {
                this.arrival = this.departure = null
            }
        },
        updateDeparture ($event) {
            const date = $event?.detail.date
            this.showStayLengthMaxErrorMsg = false
            if (date) {
                if (date <= this.arrivalDate) {
                    this.departure = null
                    this.arrival = date.toISOString().slice(0,10)
                    return
                } else if (this.getStayLength(this.arrivalDate, date) > this.stayLengthMax) {
                    this.showStayLengthMaxErrorMsg = true
                    this.stayLengthMaxErrorTimeout = setTimeout(() => {
                        this.showStayLengthMaxErrorMsg = false
                    }, this.stayLengthMaxErrorTimeDuration)
                    return
                }
                this.departure = date.toISOString().slice(0,10)
            } else {
                this.departure = null
            }
        },
        updateGuests ($event) {
            this.numberOfGuests = $event.detail.adults
            this.childAges = $event.detail.children
        },
        onSelectRatePlan ($event) {
            this.points = $event.detail.ratePlan === 'points' ? 1 : 0
        }
    },
    watch: {
        arrival (newVal) {
            this.checkInDate = newVal && toLongDateString(newVal)
        },
        departure (newVal) {
            this.checkOutDate = newVal && toLongDateString(newVal)
        },
        childAges () {
            this.numberOfChildren = this.childAges.length
        },
        points () {
            if (this.isMounted) {
                this.submit()
            }
        },
    },
    // lifecycle
    created () {
        // date
        this.startDate = normalizeDate(new Date)
        this.endDate = new Date(
            this.startDate.getUTCFullYear() + 1,
            this.startDate.getUTCMonth(),
            this.startDate.getUTCDate() - 1,
        )
        // property
        const { hasAvailabilityData, propertyRentalSourceId, roomTypes, slug: propertySlug } = JSON.parse(this.property)
        this.propertyRentalSourceId = propertyRentalSourceId
        this.propertySlug = propertySlug
        // room types
        this.roomTypeOptions = roomTypes.sort()
            .map(x => ({
                display: x > 0 ? `${x} Bedroom` : x < 0 ? 'Hotel Room' : 'Studio',
                value: x,
            }))
        this.roomTypeOptions.push({ display: 'All', value: null })
        this.roomType = null
        // search
        const search = JSON.parse(this.search)
        if (search.checkInDate) {
            this.arrival = toISODateString(search.checkInDate)
        }
        if (search.checkOutDate) {
            this.departure = toISODateString(search.checkOutDate)
        }
        Object.assign(this, search)
        // user
        Object.assign(this, JSON.parse(this.user))
        // availability
        this.hasAvailabilityData = hasAvailabilityData
        this.updateAvailability()
    },
    mounted () {
        this.submit(true)
        this.isMounted = true
    },
    // misc.
    components: {
        Btn: BtnComoponent,
        DateFields: DateFieldsComponent,
        Dropdown: DropdownComponent,
        GuestFieldsSelect: GuestFieldsComponent,
        Icon: IconComponent,
        RatePlanToggle: RatePlanToggleComponent,
    },
}

/**
 * @param {string} longDateString - A date string (M j, Y)
 * @example
 * // returns '1970-01-01'
 * toISODateString('Jan 1, 1970')
 * @returns {string} Returns a date string (Y-m-d)
 */
function toISODateString (longDateString) {
    const date = new Date(longDateString + ' 00:00:00 GMT')
    return date.toISOString().slice(0,10)
}

/**
 * @param {string} isoDateString - A date string (Y-m-d)
 * @example
 * // returns 'Jan 1, 1970'
 * toISODateString('1970-01-01')
 * @returns {string} Returns a date string (M j, Y)
 */
function toLongDateString (isoDateString) {
    const date = new Date(isoDateString)
    const tokens = date.toUTCString().split(' ')
    return `${tokens[2]} ${parseInt(tokens[1])}, ${tokens[3]}`
}
</script>

<template>
    <div class="tw-mx-auto tw-py-8 tw-space-y-8 tw-container-inner wider">
        <!-- search form -->
        <form
            class="tw-space-y-8"
            v-on:submit.prevent="submit(false)"
            v-bind:action="action"
        >
            <!-- dollars/points toggle -->
            <RatePlanToggle
                class="tw-text-right"
                v-if="hasRatePlanToggle"
                v-on:select-rate-plan="onSelectRatePlan($event)"
                v-bind="{ ratePlan }"
            />

            <!-- date/guest fieldsets & submit btn -->
            <div
                class="tw-px-4 tw-py-8 tw-space-y-4 tw-bg-e6 lg:tw-px-32"
                v-bind:class="{ 'tw-cursor-wait': loading }"
            >
                <p
                    class="
                        tw-flex
                        tw-items-center
                        tw-px-3
                        tw-py-1
                        tw-m-0
                        tw-space-x-1.5
                        tw-text-sm
                        tw-text-[#ff0000]
                        tw-bg-[#fff2f2]
                        tw-rounded-xl
                    "
                    roll="alert"
                    v-if="showStayLengthMaxErrorMsg"
                >
                    <Icon class="tw-fill-current" aria-hidden="true" v-bind="{ icon: 'alert' }"/>
                    <span>You cannot book more than 28 nights!</span>
                </p>
                <div
                    class="
                        tw-relative
                        tw-space-y-3
                        lg:tw-grid
                        lg:tw-grid-cols-[2fr,1fr,1fr]
                        lg:tw-gap-3
                        lg:tw-space-y-0
                    "
                >
                    <DateFields
                        v-on:update-arrival="updateArrival"
                        v-on:update-departure="updateDeparture"
                        v-on:update-search="submit"
                        v-bind="{
                            revertExclusionSelectors: ['#' + submitBtnId],
                            // availability
                            availability,
                            hasAvailabilityData,
                            isGettingAvailability,
                            // dates
                            arrival,
                            departure,
                            startDate,
                            endDate,
                            // rate plan
                            ratePlan,
                        }"
                        v-bind:fields="datefields"
                    >
                        <template v-slot:datepickerHeader>
                            <Dropdown
                                class="
                                    tw-ml-0
                                    tw-w-full
                                    sm:tw-w-auto
                                "
                                v-if="hasAvailabilityData"
                                v-bind="roomTypeDropdown"
                                v-on:select="setRoomType($event.detail.value)"
                            />
                        </template>
                    </DateFields>
                    <GuestFieldsSelect
                        v-on:update-guests="updateGuests"
                        v-bind:guests="guests"
                    />
                    <Btn
                        v-bind="{
                            id: submitBtnId,
                            isSecondary: true,
                            isDisabled: !arrival || !departure || !numberOfGuests,
                            type: 'submit',
                        }"
                        v-bind:class="{
                            'tw-cursor-wait': loading,
                            'tw-mix-blend-multiply': !arrival || !departure || !numberOfGuests,
                        }"
                    >
                        Check Availability
                    </Btn>
                </div>
            </div>
        </form>

        <!-- loading msg -->
        <div
            v-show="loading"
            class="
                tw-flex
                tw-flex-col
                tw-justify-center
                tw-items-center
                tw-px-8
                tw-py-4
                tw-space-y-4
                tw-text-center
                tw-bg-white
                tw-border
                tw-border-solid
                tw-border-e6
            "
        >
            <div class="tw-text-2xl">Searching for your next stay!</div>
            <div class="tw-loader"></div>
        </div>

        <!-- no search query msg -->
        <div
            v-show="!stayLength"
            class="
                tw-px-8
                tw-py-4
                tw-space-y-4
                tw-text-center
                tw-bg-white
                tw-border
                tw-border-solid
                tw-border-e6
            "
        >
            <div class="tw-text-2xl">Please enter your travel dates.</div>
            <p>We need your check-in and check-out dates to look up availability and pricing of residences at this resort.</p>
        </div>

        <!-- results -->
        <div
            v-html="resultsHTML"
            v-show="resultsHTML"
            v-bind:x-data="JSON.stringify({ isPointsMember, loggedIn, points })"
        ></div>

        <!-- error msg -->
        <div
            v-show="resultsError"
            v-html="resultsErrorMarkup"
            class="
                tw-px-8
                tw-py-4
                tw-space-y-4
                tw-text-center
                tw-bg-white
                tw-border
                tw-border-solid
                tw-border-e6
                [&>h3]:tw-text-2xl
                [&>h3]:tw-font-normal
            "
        ></div>
    </div>
</template>
